/**
 * SecureEmbed Organizations Management
 *
 * CRUD page for managing organizations
 * Admin-only access
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Building2, Plus, Pencil, Trash2, RefreshCw, CheckCircle, XCircle,
  Loader2, AlertCircle, X, Search
} from 'lucide-react';
import { orgApi } from '../services/api';

interface Organization {
  orgId: string;
  name: string;
  description: string;
  status: 'enabled' | 'disabled';
  created_at: string;
}

const OrganizationsPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Toggling / deleting
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orgApi.listOrganisations();
      const orgs = (response?.organisations || []).map((o: any) => ({
        orgId: o.org_id,
        name: o.name,
        description: o.description || '',
        status: o.status === 'active' ? 'enabled' : 'disabled',
        created_at: o.created_at,
      }));
      setOrganizations(orgs);
    } catch (e: any) {
      setError(e.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await orgApi.createOrganisation({ name: createName.trim(), description: createDescription.trim() });
      setCreateName('');
      setCreateDescription('');
      setShowCreateForm(false);
      setMessage('Organization created successfully');
      setTimeout(() => setMessage(''), 3000);
      fetchOrganizations();
    } catch (e: any) {
      setError(e.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await orgApi.updateOrganisation(id, { name: editName.trim(), description: editDescription.trim() });
      setEditingId(null);
      setMessage('Organization updated');
      setTimeout(() => setMessage(''), 3000);
      fetchOrganizations();
    } catch (e: any) {
      setError(e.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (org: Organization) => {
    setActionLoading(org.orgId);
    try {
      const newStatus = org.status === 'enabled' ? 'disabled' : 'enabled';
      await orgApi.updateOrganisation(org.orgId, { status: newStatus === 'enabled' ? 'active' : 'deleted' });
      setMessage(`Organization ${newStatus}`);
      setTimeout(() => setMessage(''), 3000);
      fetchOrganizations();
    } catch (e: any) {
      setError(e.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (org: Organization) => {
    if (!confirm(`Are you sure you want to delete "${org.name}"?\n\nThis action cannot be undone.`)) return;
    setActionLoading(org.orgId);
    try {
      await orgApi.deleteOrganisation(org.orgId);
      setMessage('Organization deleted');
      setTimeout(() => setMessage(''), 3000);
      fetchOrganizations();
    } catch (e: any) {
      setError(e.message || 'Failed to delete organization');
    } finally {
      setActionLoading(null);
    }
  };

  const startEdit = (org: Organization) => {
    setEditingId(org.orgId);
    setEditName(org.name);
    setEditDescription(org.description);
  };

  const filtered = organizations.filter(o =>
    o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Organizations</h1>
              <p className="text-purple-200 mt-1">Manage tenant organizations and access</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowCreateForm(true); setError(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              New Organization
            </button>
            <button
              onClick={fetchOrganizations}
              disabled={loading}
              className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <p className="text-sm text-green-700">{message}</p>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Create Organization</h3>
            <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Organization name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Optional description"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !createName.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-100 rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">All Organizations</h3>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-2" />
            <p className="text-gray-500">Loading organizations...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {searchTerm ? 'No organizations match your search.' : 'No organizations yet. Click "New Organization" to create one.'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((org) => (
                <tr key={org.orgId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    {editingId === org.orgId ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{org.name}</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingId === org.orgId ? (
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
                      />
                    ) : (
                      <span className="text-sm text-gray-600">{org.description || '-'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleStatus(org)}
                      disabled={actionLoading === org.orgId}
                      className="flex items-center gap-1.5"
                    >
                      {actionLoading === org.orgId ? (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      ) : org.status === 'enabled' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                      <span className={`text-sm ${org.status === 'enabled' ? 'text-green-700' : 'text-gray-500'}`}>
                        {org.status === 'enabled' ? 'Enabled' : 'Disabled'}
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {editingId === org.orgId ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleSaveEdit(org.orgId)}
                          disabled={saving}
                          className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(org)}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(org)}
                          disabled={actionLoading === org.orgId}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OrganizationsPage;
